
import { ROUTENAMES } from "@models/constants/routes";
import { apiService } from "@services/api.service";
import { ciTrackingService } from "@services/ci-tracking.service";
import { computed, defineComponent, getCurrentInstance, onBeforeMount, PropType, Ref, ref, watch } from "vue";
import { useRouter } from "vue-router/composables";
import { useLoader } from "@composables/loader";
import { useI18n } from "@composables/i18n";
import { MetaInfo } from "vue-meta";
import { alternateService } from "@services/alternate.service";
import CiCompanyTile from "@components/companies/CompanyTile.vue";

enum CompanyProfileTypeRoutes {
  "asset_manager" = ROUTENAMES.COMPANIES_ASSET_MANAGERS_INDEX,
  "wealth_manager" = ROUTENAMES.COMPANIES_WEALTH_MANAGERS_INDEX,
  "financial_service_provider" = ROUTENAMES.COMPANIES_SERVICE_PROVIDERS_INDEX,
}

enum CompanyProfileTypeTrackingIdentifier {
  "asset_manager" = 0,
  "wealth_manager" = 1,
  "financial_service_provider" = 2,
}

type CompanyProfileTypes = keyof typeof CompanyProfileTypeTrackingIdentifier;

export default defineComponent({
  name: "CiCompanyProfiles",
  components: {
    "ci-company-tile": CiCompanyTile,
  },
  metaInfo(): MetaInfo {
    return {
      title: this.metaOptions.metaTitle,
      link: alternateService.fromRoute(this.$route),
      meta: [
        { vmid: "description", name: "description", content: this.metaOptions.metaDescription },
        { vmid: "og:title", property: "og:title", content: this.metaOptions.metaTitle },
        { vmid: "og:description", property: "og:description", content: this.metaOptions.metaDescription },
        { vmid: "twitter:title", name: "twitter:title", content: this.metaOptions.metaTitle },
        { vmid: "twitter:description", name: "twitter:description", content: this.metaOptions.metaDescription },
      ],
    };
  },
  props: {
    type: {
      type: String as PropType<CompanyProfileTypes>,
      default: "asset_manager",
    },
  },
  setup(props) {
    const companyType = ref(props.type);
    const currentTab = ref(0);
    const router = useRouter();
    const loader = useLoader();
    const { t } = useI18n(getCurrentInstance());

    const metaOptions: Ref<Record<string, any>> = computed(() => ({
      metaTitle: t(`ui.views.company_profiles.index.${props.type}s`) as string,
      metaDescription: t("ui.views.company_profiles.index.meta_tags.description") as string,
    }));

    const profiles: Ref<Record<CompanyProfileTypes, Companies.Tile[]>> = ref({
      asset_manager: [],
      financial_service_provider: [],
      wealth_manager: [],
    });

    const fetchCompanyProfiles = async (type: CompanyProfileTypes) => {
      loader.start();

      try {
        await ciTrackingService.trackView("CompanyOverview", CompanyProfileTypeTrackingIdentifier[type].toString());
        if (profiles.value[type] === undefined || profiles.value[type].length === 0) {
          const response = await apiService.companies.all(type);
          profiles.value[type] = response.data.company_profiles;
        }
      } catch (err) {
        apiService.handleAPIError(err);
      } finally {
        loader.stop();
      }
    };

    watch(companyType, async () => {
      // changing the url when using the tabs
      router.replace(router.resolve({ name: CompanyProfileTypeRoutes[companyType.value].toString() }).location);
    });
    watch(
      () => props.type,
      async () => {
        // fetching on url change
        await fetchCompanyProfiles(props.type);
      }
    );

    onBeforeMount(async () => {
      await fetchCompanyProfiles(companyType.value);
    });

    return { currentTab, profiles, companyType, metaOptions };
  },
});
